/**
 * Unified Type Registry for DynamicCriteriaGenerator
 *
 * Single source of truth for all criteria component type metadata.
 * This file contains NO React imports to avoid SSR issues.
 *
 * Component rendering logic is in componentRegistry.tsx which imports
 * from this file and adds the React Component references.
 */

import { CriteriaComponentType } from 'src/redux/slices/aiListGeneratorSlice';

/**
 * Type classification for component behavior
 */
export type ComponentTypeClass = 'static' | 'singleton' | 'regular';

/**
 * Complete metadata for a criteria component type.
 * All configuration needed for data transformation, rendering, and UI.
 */
export interface TypeMetadata {
  // === Data Field Configuration ===
  /** Field name used in Redux criteria_inputs for API submission */
  reduxDataField: string;
  /** Key used for initialData prop passed to component */
  initialDataPropKey: string;

  // === Data Extraction Functions ===
  /** Extract IDs from an array of items for Redux storage */
  getItemIds: (items: any[]) => any;
  /** Extract a single item's ID (for duplicate checking in slash menu) */
  getItemId: (item: any) => string;

  // === Component Behavior ===
  /** Default connector value */
  defaultConnector: 'ANY' | 'ALL';
  /** Component type classification */
  typeClass: ComponentTypeClass;

  // === UI Configuration ===
  /** Human-readable label for menus and tooltips */
  label: string;
  /** Tailwind color theme (e.g., 'sky', 'green', 'pink') */
  color: string;
  /** Description text for menus */
  description: string;
}

/**
 * Master registry of all criteria component types.
 * This is the SINGLE SOURCE OF TRUTH for type configuration.
 */
export const TYPE_REGISTRY: Record<CriteriaComponentType, TypeMetadata> = {
  codes: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialCodes',
    getItemIds: (items) => items.map((c: { code_name: string }) => c.code_name),
    getItemId: (item) => item.code_name || item.code || item.id,
    defaultConnector: 'ANY',
    typeClass: 'regular',
    label: 'Codes',
    color: 'green',
    description: 'Add a code type',
  },

  code_set: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialCodeSets',
    getItemIds: (items) => items[0]?.id,
    getItemId: (item) => item.id,
    defaultConnector: 'ALL',
    typeClass: 'singleton',
    label: 'Code Set',
    color: 'pink',
    description: 'Add a code set',
  },

  crm_status: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialCRMStatuses',
    getItemIds: (items) => items.map((s: { id: string }) => s.id),
    getItemId: (item) => item.id,
    defaultConnector: 'ALL',
    typeClass: 'regular',
    label: 'CRM Status',
    color: 'teal',
    description: 'Add a CRM Status to your criteria',
  },

  education_focus: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialEducationFocus',
    getItemIds: (items) => items[0]?.value || items[0],
    getItemId: (item) => item?.value || item,
    defaultConnector: 'ANY',
    typeClass: 'singleton',
    label: 'Education Focus',
    color: 'rose',
    description: 'Add a training focus (e.g. Cardiology)',
  },

  education_program_type: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialEducationProgramType',
    getItemIds: (items) => items[0]?.value || items[0],
    getItemId: (item) => item?.value || item,
    defaultConnector: 'ANY',
    typeClass: 'singleton',
    label: 'Education Program Type',
    color: 'cyan',
    description: 'Select medical school, residency, or fellowship',
  },

  educational_institution: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialEducationalInstitutions',
    getItemIds: (items) => items.map((i: { id: string | number }) => String(i.id)),
    getItemId: (item) => String(item?.id ?? item),
    defaultConnector: 'ANY',
    typeClass: 'regular',
    label: 'Educational Institution',
    color: 'amber',
    description: 'Add educational institutions',
  },

  entity_attribute: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialEntityAttribute',
    getItemIds: (items) => items[0]?.value || items[0],
    getItemId: (item) => item.value || item,
    defaultConnector: 'ANY',
    typeClass: 'singleton',
    label: 'Site of Care Attribute',
    color: 'rose',
    description: 'Select a site of care attribute',
  },

  entity_type: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialEntityType',
    getItemIds: (items) => items[0]?.value || items[0],
    getItemId: (item) => item.value || item,
    defaultConnector: 'ANY',
    typeClass: 'singleton',
    label: 'Entity Type',
    color: 'cyan',
    description: 'Select providers or sites',
  },

  filter_set: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialFilterSets',
    getItemIds: (items) => items.map((f: { id: string }) => f.id),
    getItemId: (item) => item.id,
    defaultConnector: 'ALL',
    typeClass: 'regular',
    label: 'Filter Sets',
    color: 'indigo',
    description: 'Add a filter set',
  },

  open_payments: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialOpenPayments',
    getItemIds: (items) => items.map((p: { id: string }) => p.id),
    getItemId: (item) => item.id,
    defaultConnector: 'ANY',
    typeClass: 'regular',
    label: 'Open Payments',
    color: 'yellow',
    description: 'Add open payments companies',
  },

  open_payments_category: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialOpenPaymentsCategory',
    getItemIds: (items) => items.map((p: { id: string }) => p.id),
    getItemId: (item) => item.id,
    defaultConnector: 'ANY',
    typeClass: 'regular',
    label: 'Open Payments Category',
    color: 'violet',
    description: 'Add open payments category',
  },

  payer_name: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialPayerNames',
    getItemIds: (items) => items.map((p: { id: string }) => p.id),
    getItemId: (item) => item.id,
    defaultConnector: 'ANY',
    typeClass: 'regular',
    label: 'Payer Name',
    color: 'sky',
    description: 'Add a payer name',
  },

  payer_type: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialPayerTypes',
    getItemIds: (items) => items.map((p: { name: string }) => p.name),
    getItemId: (item) => item.name,
    defaultConnector: 'ANY',
    typeClass: 'regular',
    label: 'Payer Type',
    color: 'pink',
    description: 'Add a payer type',
  },

  provider_cohort: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialEncounters',
    getItemIds: (items) => items[0]?.id,
    getItemId: (item) => item.id,
    defaultConnector: 'ALL',
    typeClass: 'singleton',
    label: 'Encounters',
    color: 'orange',
    description: 'Add an encounter',
  },

  provider_list: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialProviderLists',
    getItemIds: (items) => items.map((l: { id: string }) => l.id),
    getItemId: (item) => item.id,
    defaultConnector: 'ANY',
    typeClass: 'regular',
    label: 'Provider List',
    color: 'lime',
    description: 'Add a provider list',
  },

  providers: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialProviders',
    getItemIds: (items) =>
      items.map((p: { provider_id: string }) => p.provider_id),
    getItemId: (item) => item.provider_id || item.id,
    defaultConnector: 'ANY',
    typeClass: 'regular',
    label: 'Provider',
    color: 'sky',
    description: 'Add a provider',
  },

  specialties: {
    reduxDataField: 'value',
    initialDataPropKey: 'initialSpecialties',
    getItemIds: (items) => items.map((s: { id: string }) => s.id),
    getItemId: (item) => item.id,
    defaultConnector: 'ANY',
    typeClass: 'regular',
    label: 'Specialty',
    color: 'emerald',
    description: 'Add a specialty',
  },

  this_provider: {
    reduxDataField: 'value',
    initialDataPropKey: '',
    getItemIds: () => undefined,
    getItemId: () => 'this_provider',
    defaultConnector: 'ANY',
    typeClass: 'static',
    label: 'This Provider',
    color: 'purple',
    description: 'Reference the current provider',
  },

  this_site: {
    reduxDataField: 'value',
    initialDataPropKey: '',
    getItemIds: () => undefined,
    getItemId: () => 'this_site',
    defaultConnector: 'ANY',
    typeClass: 'static',
    label: 'This Site',
    color: 'teal',
    description: 'Reference the current site',
  },
};

// === Derived Constants ===

/** All criteria component types */
export const ALL_TYPES = Object.keys(TYPE_REGISTRY) as CriteriaComponentType[];

/** Static component types (no data selection needed) */
export const STATIC_TYPES = ALL_TYPES.filter(
  (type) => TYPE_REGISTRY[type].typeClass === 'static'
);

/** Singleton component types (only one item allowed) */
export const SINGLETON_TYPES = ALL_TYPES.filter(
  (type) => TYPE_REGISTRY[type].typeClass === 'singleton'
);

// === Helper Functions ===

/**
 * Get metadata for a component type
 */
export function getTypeMetadata(type: CriteriaComponentType): TypeMetadata {
  return TYPE_REGISTRY[type];
}

/**
 * Get the human-readable label for a type
 */
export function getTypeLabel(type: CriteriaComponentType): string {
  return TYPE_REGISTRY[type].label;
}

/**
 * Check if a type is static (no data needed)
 */
export function isStaticType(type: CriteriaComponentType): boolean {
  return TYPE_REGISTRY[type].typeClass === 'static';
}

/**
 * Check if a type is singleton (only one item allowed)
 */
export function isSingletonType(type: CriteriaComponentType): boolean {
  return TYPE_REGISTRY[type].typeClass === 'singleton';
}

/**
 * Get the effective type, handling ui_key for this_site stored as this_provider
 */
export function getEffectiveType(
  type: CriteriaComponentType,
  uiKey?: string
): CriteriaComponentType {
  if (uiKey === 'this_site') {
    return 'this_site';
  }
  return type;
}

/**
 * Generate menu items from the registry for a given set of allowed types
 */
export function getMenuItems(allowedTypes: CriteriaComponentType[]) {
  return allowedTypes
    .map((type) => ({
      type,
      label: TYPE_REGISTRY[type].label,
      color: TYPE_REGISTRY[type].color,
      description: TYPE_REGISTRY[type].description,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
